import React from "react";
import { Link } from "react-router-dom";
import {
  ShieldCheck,
  Shield,
  BarChart3,
  Wrench,
  Globe2,
  Landmark,
  FileCheck2,
  ArrowRight,
  PlayCircle,
} from "lucide-react";

const videoCards = [
  {
    eyebrow: "See the Platform in Action",
    description:
      "Understand how AI Fine Protector helps teams identify exposure, model fine risk and plan remediation.",
    youtubeEmbed: "https://www.youtube.com/embed/N9ks0l6qI6E?start=6",
    title: "AI Regulatory Compliance: A Risk Reduction Tool",
  },
  {
    eyebrow: "Understand the Audit Flow",
    description:
      "Watch how the free audit experience helps surface practical compliance risks before they escalate.",
      youtubeEmbed: "https://www.youtube.com/embed/-Tm_tjw6fUI",
    title: "AI Fine Protector Risk Remediation Tool",
  },
  {
    eyebrow: "AI Regulatory Scrutiny Is Global",
    description: "Real U.S. and European enforcement cases and why regulatory scrutiny matters.",
    youtubeEmbed: "https://www.youtube.com/embed/R_dUAH2q_TI",
    title: "AI Regulatory Scrutiny Is Global | AI Fine Protector ™",
  },
];

const featureCards = [
  {
    icon: Shield,
    title: "Compliance Risk Analysis",
    description:
      "Identify where your AI use may create regulatory exposure or fines",
  },
  {
    icon: BarChart3,
    title: "Regulatory Risk Breakdown",
    description:
      "See how specific activities translate into measurable regulatory fines",
  },
  {
    icon: Wrench,
    title: "Risk Reduction Strategy",
    description:
      "Translate identified risks into clear, practical remediation steps.",
  },
  {
    icon: Globe2,
    title: "Multi-Jurisdiction Coverage",
    description:
      "Analyze exposure across US, U.K., EU and other emerging AI regulatory regimes",
  },
  {
    icon: Landmark,
    title: "Financial Risk Exposure",
    description:
      "Translate compliance gaps into structured estimates of potential fines",
  },
  {
    icon: FileCheck2,
    title: "Audit Ready Reporting",
    description:
      "Reports designed for internal review and defence against regulators",
  },
];

function BrandMark() {
  return (
    <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-slate-950 shadow-[0_10px_24px_rgba(15,23,42,0.18)]">
      <ShieldCheck className="h-5 w-5 text-white" />
    </div>
  );
}

function VideoCard({ eyebrow, description, youtubeEmbed, title }) {
  return (
    <div className="overflow-hidden rounded-[18px] border-2 border-blue-600 bg-white shadow-[0_20px_45px_rgba(37,99,235,0.20)]">
      <div className="border-b border-blue-200 bg-white px-4 py-3">
        <div className="flex items-start gap-2">
          <div className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border border-blue-200 bg-blue-50">
            <PlayCircle className="h-3.5 w-3.5 text-blue-700" />
          </div>
          <div>
            <p className="text-[12px] font-semibold text-slate-900">{eyebrow}</p>
            <p className="mt-0.5 text-[11px] leading-5 text-slate-500">{description}</p>
          </div>
        </div>
      </div>

      <div className="bg-slate-950 p-1.5">
        <div className="overflow-hidden rounded-[14px] border border-slate-800 shadow-[inset_0_1px_0_rgba(255,255,255,0.06)]">
          <iframe
            className="aspect-video w-full"
            src={youtubeEmbed}
            title={title}
            loading="lazy"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
          />
        </div>
      </div>
    </div>
  );
}

function FeatureCard({ icon: Icon, title, description }) {
  return (
    <div className="rounded-[18px] border-2 border-blue-600 bg-white p-6 text-center shadow-[0_18px_40px_rgba(37,99,235,0.18)]">
      <div className="mb-4 flex justify-center">
        <div className="flex h-10 w-10 items-center justify-center rounded-xl border border-blue-200 bg-blue-50 shadow-[0_6px_16px_rgba(59,130,246,0.12)]">
          <Icon className="h-5 w-5 text-blue-700" />
        </div>
      </div>
      <h3 className="text-[14px] font-semibold text-slate-900">{title}</h3>
      <p className="mt-2 text-[12px] leading-5 text-slate-600">{description}</p>
    </div>
  );
}

export default function LandingPage() {
  return (
    <div className="min-h-screen bg-[#f8fafc] text-slate-900">
      <div className="mx-auto max-w-[1180px] px-5 sm:px-7 lg:px-10">
        <nav className="flex flex-wrap items-center justify-between gap-4 py-5" aria-label="Main navigation">
          <Link to="/" className="flex items-center gap-3"><BrandMark /><div><p className="text-base font-bold tracking-tight text-blue-950">AI Fine Protector ™</p><p className="text-xs text-slate-500">The AI Risk Management Platform</p></div></Link>
          <div className="flex flex-wrap items-center gap-2 text-xs font-semibold">
            <Link to="/" className="rounded-lg px-3 py-2 text-blue-950 hover:bg-blue-50">Home</Link>
            <a href="#why-afp" className="rounded-lg px-3 py-2 text-blue-950 hover:bg-blue-50">Why AFP</a>
            <a href="#videos" className="rounded-lg px-3 py-2 text-blue-950 hover:bg-blue-50">How It Works</a>
            <Link to="/pricing" className="rounded-lg px-3 py-2 text-blue-950 hover:bg-blue-50">Pricing</Link>
            <Link to="/auth?intent=affiliate&next=%2Fsales-affiliate%2Fapplication" className="rounded-lg px-3 py-2 text-blue-950 hover:bg-blue-50">For Sales Affiliates</Link>
            <Link to="/auth?intent=subscriber" className="rounded-xl border border-blue-600 px-4 py-2 text-blue-700 hover:bg-blue-50">Subscriber Access</Link>
            <Link to="/universal-audit" className="rounded-xl bg-orange-600 px-4 py-2 text-white hover:bg-orange-700">Run Free Universal Audit</Link>
          </div>
        </nav>
      </div>
      <section className="relative overflow-hidden border-y border-sky-100 bg-gradient-to-r from-white via-sky-50 to-blue-100">
        <div className="absolute inset-y-0 right-0 hidden w-[48%] bg-cover bg-center lg:block" style={{ backgroundImage: 'url(/afp-global-hero-approved-crop.png)' }} role="img" aria-label="Blue globe with United States Capitol, European Union flags, and London's Big Ben" />
        <div className="absolute inset-y-0 right-[36%] hidden w-[25%] bg-gradient-to-r from-sky-50 to-transparent lg:block" />
        <div className="relative mx-auto max-w-[1320px] px-5 py-12 sm:px-7 lg:px-10 lg:py-16">
          <div className="max-w-[710px] lg:w-[61%]">
            <h1 className="text-4xl font-extrabold leading-tight tracking-tight text-blue-950 sm:text-5xl">AI Creates Opportunities.<br />It Also Creates Regulatory Risk.</h1>
            <p className="mt-5 text-base leading-7 text-slate-700">AI regulations are expanding across the U.S., European Union and United Kingdom. AI Fine Protector ™ helps businesses understand, quantify and manage potential AI-related regulatory and financial exposure.</p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link to="/universal-audit" className="inline-flex min-h-12 items-center justify-center gap-2 rounded-2xl bg-orange-600 px-6 py-3 font-bold text-white shadow-lg hover:bg-orange-700">Run Free Universal Risk Audit <ArrowRight className="h-4 w-4" /></Link>
              <a href="#videos" className="inline-flex min-h-12 items-center justify-center gap-2 rounded-2xl border-2 border-blue-600 bg-white px-6 py-3 font-semibold text-blue-700 hover:bg-blue-50"><PlayCircle className="h-4 w-4" /> See How It Works</a>
            </div>
            <p className="mt-3 text-xs text-slate-600">No credit card required. Get a high-level risk estimate in minutes.</p>
          </div>
        </div>
      </section>
      <div className="mx-auto max-w-[1180px] px-5 sm:px-7 lg:px-10">
        <section id="videos" className="py-10">
          <div className="text-center"><h2 className="text-3xl font-bold tracking-tight text-blue-950">Learn Why AI Regulatory Risk Matters</h2><p className="mx-auto mt-2 max-w-[760px] text-sm leading-6 text-slate-600">Watch our short videos to see real-world examples, key risks, and how AI Fine Protector ™ can help.</p></div>
          <div className="mt-7 grid gap-5 lg:grid-cols-3">
            {videoCards.map((video, index) => <div key={video.title} className="relative"><span className="absolute -left-2 -top-3 z-10 flex h-10 w-10 items-center justify-center rounded-full border-2 border-white bg-blue-600 text-lg font-bold text-white shadow-lg">{index + 1}</span><VideoCard {...video} /></div>)}
          </div>
          {/** YouTube Private videos cannot play for public visitors. Visibility is a separate publication gate. */}
        </section>
        <section id="why-afp" className="pb-8">
          <div className="text-center">
            <h2 className="text-[32px] font-semibold tracking-tight text-slate-950">
              Comprehensive AI Compliance Protection
            </h2>
            <p className="mx-auto mt-2 max-w-[710px] text-[12px] leading-6 text-slate-600">
              Built to help organizations understand AI exposure, quantify potential fine risk and prepare more confidently for internal and regulatory review.
            </p>
          </div>

          <div className="mt-6 grid gap-5 md:grid-cols-2 xl:grid-cols-3">
            {featureCards.map((card) => (
              <FeatureCard key={card.title} {...card} />
            ))}
          </div>

          <div className="mx-auto mt-6 max-w-[760px] text-center">
            <p className="text-[13px] font-semibold leading-6 text-slate-700">
              Your data is protected using industry-standard encryption and secure infrastructure.
            </p>
            <p className="mt-1 text-[12px] leading-6 text-slate-500">
              Information is handled with strict access controls and is never shared or used outside your assessment.
            </p>
          </div>
        </section>

        <section className="pb-12 pt-2">
          <div className="mx-auto max-w-[980px] rounded-[24px] border border-sky-200 bg-gradient-to-b from-slate-50 to-sky-50/70 px-6 py-10 shadow-[0_20px_38px_rgba(37,99,235,0.10)]">
            <div className="mx-auto max-w-[560px] text-center">
              <h2 className="text-[32px] font-semibold tracking-tight text-slate-950">
                Stay Ahead of AI Compliance Risks
              </h2>
              <p className="mt-2 text-[13px] leading-6 text-slate-600">
                Get your free AI risk audit today
              </p>

              <div className="mt-6 grid gap-3 sm:grid-cols-3">
                <Link
                  to="/universal-audit"
                  className="inline-flex min-h-11 items-center justify-center rounded-2xl bg-emerald-600 px-5 py-3 text-center text-[12px] font-semibold leading-5 text-white shadow-[0_10px_24px_rgba(5,150,105,0.24)] transition hover:bg-emerald-700"
                >
                  Run Free Universal Audit
                </Link>
                <Link
                  to="/auth?intent=subscriber"
                  className="inline-flex min-h-11 items-center justify-center rounded-2xl border border-slate-300 bg-white px-5 py-3 text-center text-[12px] font-semibold leading-4 text-slate-800 shadow-sm transition hover:border-slate-400 hover:bg-slate-50"
                >
                  <span>
                    <span className="block">Subscriber Access</span>
                    <span className="mt-0.5 block text-[10px] font-medium text-slate-500">New &amp; Existing Subscribers</span>
                  </span>
                </Link>
                <Link
                  to="/auth?intent=affiliate&next=%2Fsales-affiliate%2Fapplication"
                  className="inline-flex min-h-11 items-center justify-center rounded-2xl bg-blue-600 px-5 py-3 text-center text-[12px] font-semibold leading-4 text-white shadow-[0_10px_24px_rgba(37,99,235,0.26)] transition hover:bg-blue-700"
                >
                  <span>
                    <span className="block">Sales Affiliate Access</span>
                    <span className="mt-0.5 block text-[10px] font-medium text-blue-100">New &amp; Existing Sales Affiliates</span>
                  </span>
                </Link>
              </div>
            </div>
          </div>
        </section>

        <footer className="py-4 text-center text-[10px] text-slate-500">
          <p>
            <span className="font-semibold text-slate-600">Contact Us:</span>{" "}
            <a href="mailto:info@aifineprotector.com" className="font-semibold text-blue-700 transition hover:text-blue-800 hover:underline">
              info@aifineprotector.com
            </a>
          </p>
          <p className="mt-1">© 2026 Advanced Advisory Services, LLC. All Rights Reserved.</p>
        </footer>
      </div>
    </div>
  );
}
